import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { logo } from "../../assets/Exportimageicon";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu, X, Bell } from "lucide-react";

import service from "../../services/service";
import endpoints from "../../services/endpoints";
import { logout } from "../../features/auth/authSlice";
import { setNotifications } from "../../features/notifications/notificationSlice"; // Imported Redux Action

// ==========================================
// UTILITY HELPERS FOR DYNAMIC FORMATTING
// ==========================================
const formatNotifType = (typeString) => {
  if (!typeString) return "Notification";
  return typeString
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatNotifDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
  const timeStr = date.toLocaleTimeString('en-US', timeOptions);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${timeStr} | ${day} ${month}, ${year}`;
};

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // =========================
  // REDUX SELECTION
  // =========================
  const accessToken = useSelector((state) => state.auth.accessToken);
  const isLoggedIn = !!accessToken;

  // FIXED: Reading from the global Redux store instead of local useState
  const notifications = useSelector((state) => state.notifications.items || []);

  // =========================
  // LOCAL UI STATES
  // =========================
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [headerUser, setHeaderUser] = useState(null);

  const dropdownRef = useRef();
  const notifRef = useRef();

  const authRoutes = ["/login", "/signup", "/create-account", "/forget-password", "/reset-password", "/verify-email"];
  const isAuthPage = authRoutes.includes(location.pathname);

  // FETCH PROFILE
  useEffect(() => {
    const fetchHeaderProfile = async () => {
      try {
        if (!accessToken) return;
        const res = await service.get(endpoints.user.headerProfile, {}, {
          headers: { Authorization: `Bearer ${accessToken}` },
          skipToast: true,
        });
        setHeaderUser(res?.data?.data);
      } catch (error) {
        console.log("HEADER PROFILE ERROR", error);
      }
    };
    fetchHeaderProfile();
  }, [accessToken]);


  useEffect(() => {
    const fetchNotificationsList = async () => {
      try {
        if (!accessToken) return;
        const res = await service.get(endpoints.notifications.viewAll, {}, {
          headers: { Authorization: `Bearer ${accessToken}` },
          skipToast: true,
        });

        dispatch(setNotifications(res?.data?.data || []));
      } catch (error) {
        console.log("FETCH NOTIFICATIONS ERROR", error);
      }
    };

    fetchNotificationsList();
  }, [accessToken, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
      if (notifRef.current && !notifRef.current.contains(event.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when resizing to desktop; lock body scroll while open
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogin = () => navigate("/login");
  const handleDashboard = () => {
    navigate("/profile");
    setDropdownOpen(false);
    setMobileOpen(false);
  };
  const closeMobileMenu = () => setMobileOpen(false);

  const handleLogout = async () => {
    try {
      await service.post(endpoints.auth.logout, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
        skipToast: true,
      });
      dispatch(logout());
      setDropdownOpen(false);
      setMobileOpen(false);
      navigate("/login");
    } catch (error) {
      dispatch(logout());
      navigate("/login");
    }
  };

  return (
    <header className={`main-header ${isAuthPage ? "auth-header" : "website-header"}`}>
      <div className="logo" onClick={() => navigate("/home")}>
        <img src={logo} alt="Logo" />
      </div>

      <nav className={mobileOpen ? "mobile-open" : ""} aria-hidden={!mobileOpen}>
        <ul className="nav-links">
          <li><NavLink to="/home" onClick={closeMobileMenu}>Home</NavLink></li>
          <li><NavLink to="/challenges" onClick={closeMobileMenu}>Challenge</NavLink></li>
          <li><NavLink to="/contact" onClick={closeMobileMenu}>Contact Us</NavLink></li>
          <li><NavLink to="/community" onClick={closeMobileMenu}>Community</NavLink></li>
          {!isLoggedIn ? (
            <li className="mobile-auth-link">
              <button type="button" onClick={() => { handleLogin(); closeMobileMenu(); }}>LOGIN</button>
            </li>
          ) : (
            <>
              <li className="mobile-auth-link">
                <button type="button" onClick={handleDashboard}>View Dashboard</button>
              </li>
              <li className="mobile-auth-link logout-link">
                <button type="button" onClick={handleLogout}>Logout</button>
              </li>
            </>
          )}
        </ul>
      </nav>

      <div className="header-right">
        {!isLoggedIn ? (
          <button onClick={handleLogin} className="login-btn">LOGIN</button>
        ) : (
          <>
            <div className="notification-wrapper" ref={notifRef}>
              <button className="notification-bell-btn" onClick={() => { setNotifOpen(!notifOpen); setDropdownOpen(false); }}>
                <Bell size={22} className="bell-icon" />
                {notifications.some(n => n.isRead === false) && <span className="bell-dot"></span>}
              </button>

              {notifOpen && (
                <div className="notification-dropdown">
                  <div className="notif-header">
                    <span className="notif-title">Notifications</span>
                    <button className="get-all-btn" onClick={() => { navigate("/notifications"); setNotifOpen(false); }}>View All</button>
                  </div>
                  <div className="notif-list">
                    {notifications.length === 0 ? (
                      <div className="no-notif-message">No new notifications</div>
                    ) : (
                      /* Show only up to 3 inside the mini header dropdown dropdown safely */
                      notifications.slice(0, 3).map((notif) => {
                        const isUnread = notif.isRead === false;
                        return (
                          <div key={notif.id || notif._id} className={`notif-item ${isUnread ? "unread-notif" : ""}`}>
                            <div className="notif-body">
                              <h4 className="notif-item-title">{formatNotifType(notif.type)}</h4>
                              <p className="notif-item-desc">{notif.message}</p>
                              <span className="notif-item-time">{formatNotifDate(notif.createdAt || notif.created_at)}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="user-profile-wrapper" ref={dropdownRef}>
              <button className="user-profile-btn" onClick={() => { setDropdownOpen(!dropdownOpen); setNotifOpen(false); }}>
                <img src={headerUser?.profileImage ? headerUser.profileImage : `https://ui-avatars.com/api/?name=${encodeURIComponent(headerUser?.fullName || "JOHN")}&background=random&color=fff`} alt="Profile" />
                <span className="user-profile-name">{headerUser?.fullName || "Shubham Modi"}</span>
              </button>
              {dropdownOpen && (
                <div className="user-dropdown">
                  <button className="dropdown-item" onClick={handleDashboard}>View Dashboard</button>
                  <button className="dropdown-item" onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          </>
        )}
        <button
          type="button"
          className="mobile-menu-btn"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => {
            setMobileOpen((open) => !open);
            setDropdownOpen(false);
            setNotifOpen(false);
          }}
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>
    </header>
  );
};

export default Header;